import React from 'react';

import ServiceHandler from '@pharmaplan/common/helpers/ServiceHandler';
import { dropdownMapper } from '@pharmaplan/common/helpers/utils';

import {
  ISoftware,
  IBanners,
  IProvinces,
  IContactPreferences,
  IUtils,
  IAllRegions,
  ServiceTypes,
  TypeOfUser,
} from '@pharmaplan/common';

import {
  Constants,
  InputTypes,
  telephoneInputMask,
} from '../../helpers/Constants';

import { ReactComponent as UploadIcon } from '../../assets/svg/uploadCloud.svg';
import { IPharmacistSignupForm, IPharmacySignupForm } from '../Profile/types';
import { fileUploadTypes } from '../../hooks/FileUpload/useFileUpload';
import {
  hospitalDischargeComfortLevels,
  LanguageLevel,
  withTechnicianContact,
} from '../Profile/ProfileForm/Forms';
import strings from '../../localization';

import style from './styles';

const year = new Date().getFullYear();
const { outlined } = Constants.variant;
const { uploadIconLabel, uploadFile } = Constants.formInputType;
const isSelfService = ServiceHandler.getService() === ServiceTypes.self;

const crossPlatformSignupFields = () =>
  (isSelfService
    ? [
      {
        key: 'createPharmaPlanProfile',
        label: strings.createIdenticalPharmaPlanProfile,
        type: Constants.formInputType.checkbox,
        xs: 12,
        name: 'createPharmaPlanProfile',
        id: 'createPharmaPlanProfile',
        customClass: {
          color: '#4A5056',
        },
      },
    ]
    : []);

const years = Array.from(new Array(50), (val, index) => {
  const value = year - index;

  return { key: value, label: value };
});

export type ICommonSignupContact = typeof commonContact;

const commonContact = (type: string, requireLabel: boolean) =>
  [
    {
      name: `${type}ContactName`,
      type: Constants.formInputType.text,
      key: 'contactName',
      requireLabel,
      label: strings.name,
      xs: 6,
    },
    {
      name: `${type}ContactPhone`,
      type: Constants.formInputType.masked,
      mask: telephoneInputMask,
      requireLabel,
      key: 'contactPhone',
      maxLength: 12,
      label: strings.phone,
      xs: 6,
    },
    {
      name: `${type}ContactPhoneExt`,
      type: Constants.formInputType.text,
      key: 'contactPhoneExt',
      label: strings.phoneExt,
      maxLength: 6,
      xs: 6,
    },
    {
      name: `${type}ContactMobile`,
      type: Constants.formInputType.masked,
      key: 'contactMobile',
      maxLength: 12,
      label: strings.mobile,
      mask: telephoneInputMask,
      xs: 6,
    },
    {
      name: `${type}ContactEmail`,
      requireLabel,
      type: Constants.formInputType.text,
      key: 'contactEmail',
      label: strings.email,
      xs: 6,
    },
  ];

export const pharmacySignupFields = ({
  softwares,
  provinces,
  banners,
  contactPreferences,
  showTermsAndConditions,
  handleChange,
}: {
  softwares: ISoftware[];
  provinces: IProvinces[];
  banners: IBanners[];
  contactPreferences: IContactPreferences[];
  showTermsAndConditions: () => void;
  handleChange: (e: boolean) => void;
}): IPharmacySignupForm =>
  ({
    pharmacyInfo: [
      {
        title: strings.general.toUpperCase(),
        hideSave: true,
        form: () =>
          [
            {
              label: strings.ownerCompanyName,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'name',
              requireLabel: true,
            },
            {
              label: strings.banner,
              type: Constants.formInputType.dropdown,
              name: 'pharmacyChainId',
              xs: 6,
              requireLabel: true,
              menuItems: dropdownMapper(banners, 'pharmacyChainId', 'name'),
            },
            {
              label: strings.bannerNumber,
              type: Constants.formInputType.text,
              xs: 6,
              requireLabel: true,
              name: 'number',
            },
            {
              label: strings.preferredLanguage,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'languageId',
              id: 'languageId',
              requireLabel: true,
              disabled: true,
            },
            {
              label: strings.software,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'softwareId',
              id: 'softwareId',
              requireLabel: true,
              menuItems: dropdownMapper(softwares, 'softwareId', 'name'),
            },
            {
              label: strings.selectedContactPreference,
              type: Constants.formInputType.dropdown,
              requireLabel: true,
              xs: 6,
              multiple: true,
              name: 'selectedContactPreferences',
              id: 'selectedContactPreferences',
              menuItems: dropdownMapper(
                contactPreferences,
                'contactPreferenceId',
                'name',
              ),
            },
          ],
      },
      {
        title: strings.weekdays,
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              label: strings.volume,
              type: Constants.formInputType.text,
              xs: 12,
              requireLabel: true,
              name: 'weekVolume',
              id: 'weekVolume',
            },
            {
              label: strings.pharmacist,
              type: Constants.formInputType.opreation,
              xs: 6,
              suffix: 'week',
              name: 'Volume',
              requireLabel: true,
              opreationType: 'Pharmacists',
              id: 'weekendVolume',
            },
            {
              label: strings.technicians,
              type: Constants.formInputType.opreation,
              xs: 6,
              requireLabel: true,
              opreationType: 'Techs',
              suffix: 'week',
              name: 'Volume',
            },
          ],
      },
      {
        title: strings.weekends,
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              label: strings.volume,
              type: Constants.formInputType.text,
              xs: 12,
              requireLabel: true,
              name: 'weekendVolume',
              id: 'weekendVolume',
            },
            {
              label: strings.pharmacist,
              type: Constants.formInputType.opreation,
              xs: 6,
              requireLabel: true,
              suffix: 'weekend',
              name: 'Volume',
              opreationType: 'Pharmacists',
            },
            {
              label: strings.technicians,
              type: Constants.formInputType.opreation,
              xs: 6,
              requireLabel: true,
              opreationType: 'Techs',
              suffix: 'weekend',
              name: 'Volume',
            },
          ],
      },
    ],
    coordinates: [
      {
        title: strings.contacts.toUpperCase(),
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              key: 'phone',
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              name: 'phone',
              requireLabel: true,
              label: strings.pharmacyPhone,
              maxLength: 12,
              xs: 6,
            },
            {
              key: 'fax',
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              name: 'fax',
              label: strings.pharmacyFax,
              maxLength: 12,
              xs: 6,
            },
            {
              key: 'emergencyPhone',
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              name: 'emergencyPhone',
              requireLabel: true,
              label: strings.emergencyPhone,
              maxLength: 12,
              xs: 6,
            },
          ],
      },
      {
        title: strings.coordinates.toUpperCase(),
        hideSave: true,
        form: () =>
          [
            {
              key: 'address',
              type: Constants.formInputType.text,
              name: 'address',
              maxLength: 50,
              requireLabel: true,
              label: strings.address,
              xs: 6,
            },
            {
              key: 'city',
              type: Constants.formInputType.text,
              name: 'city',
              requireLabel: true,
              label: strings.city,
              xs: 6,
            },
            {
              key: 'postalCode',
              type: Constants.formInputType.text,
              capitalize: true,
              requireLabel: true,
              name: 'postalCode',
              customType: InputTypes.postalCode,
              label: `${strings.postalCode} (${strings.postalCodeHint})`,
              maxLength: 10,
              placeholder: ' ',
              xs: 6,
            },
            {
              key: 'province',
              type: Constants.formInputType.dropdown,
              requireLabel: true,
              name: 'province',
              menuItems: dropdownMapper(provinces, 'key', 'name'),
              label: strings.province,
              xs: 6,
            },
          ],
      },
    ],

    primary: {
      title: strings.primary,
      type: 'single',
      form: () =>
        withTechnicianContact([
          ...commonContact('primary', true),
          {
            key: 'communicationEmail',
            type: Constants.formInputType.text,
            name: 'communicationEmail',
            label: strings.andACopyTo,
            xs: 6,
            requireLabel: false,
          },
        ]),
    },
    secondary: {
      title: strings.secondary,
      type: 'single',
      form: () =>
        commonContact('secondary', false),
    },
    accounting: {
      title: strings.accounting,
      type: 'single',
      form: () =>
        [
          {
            name: 'accountingContactName',
            type: Constants.formInputType.text,
            key: 'contactName',
            requireLabel: true,
            label: strings.name,
            xs: 6,
          },
          {
            name: 'accountingContactPhone',
            type: Constants.formInputType.masked,
            mask: telephoneInputMask,
            requireLabel: true,
            key: 'contactPhone',
            maxLength: 12,
            label: strings.phone,
            xs: 6,
          },
          {
            name: 'accountingContactPhoneExt',
            type: Constants.formInputType.text,
            key: 'contactPhoneExt',
            maxLength: 6,
            label: strings.phoneExt,
            xs: 6,
          },
          {
            name: 'accountingContactMobile',
            type: Constants.formInputType.masked,
            key: 'contactMobile',
            maxLength: 12,
            label: strings.mobile,
            mask: telephoneInputMask,
            xs: 6,
          },
          {
            name: 'accountingContactEmail',
            requireLabel: true,
            type: Constants.formInputType.text,
            key: 'contactEmail',
            label: strings.email,
            xs: 6,
          },
          {
            label: strings.agreeOn,
            linkLabel: strings.termsAndConditions,
            onLinkClick: showTermsAndConditions,
            handleChange,
            type: Constants.formInputType.checkbox,
            xs: 12,
            name: 'termsAndConditions',
            id: 'termsAndConditions',
            requireLabel: true,
            customClass: {
              color: '#4A5056',
            },
          },
          ...crossPlatformSignupFields(),
        ],
    },
  });

export const pharmacistSignupFields = ({
  proUtils,
  regions,
  showTermsAndConditions,
  handleChange,
  contactPreferences,
  userType,
  imageName,
  showPictureUploadModal,
}: {
  proUtils: IUtils;
  showPictureUploadModal: () => void;
  imageName: string;
  regions: IAllRegions;
  showTermsAndConditions: () => void;
  handleChange: (e: boolean) => void;
  contactPreferences: IContactPreferences[];
  userType:TypeOfUser;
}): IPharmacistSignupForm => {
  const isPharmacist = userType === TypeOfUser.pharmacist;
  const isTechnician = userType === TypeOfUser.technician;
  // #technician-changes: only full service collects these answers.
  const isFullServicePharmacist = isPharmacist && !isSelfService;
  const isFullServiceTechnician = isTechnician && !isSelfService;
  const isFullServicePharmacistTechnician = (isPharmacist || isTechnician) && !isSelfService;
  const isStudent = userType === TypeOfUser.student;

  return ({
    general: {
      title: strings.general.toUpperCase(),
      type: 'single',
      form: () =>
        [
          {
            name: 'firstName',
            label: strings.firstName,
            requireLabel: true,
            type: Constants.formInputType.text,
            xs: 6,
          },
          {
            name: 'lastName',
            label: strings.lastName,
            requireLabel: true,
            type: Constants.formInputType.text,
            xs: 6,
          },
          {
            label: strings.preferredLanguage,
            type: Constants.formInputType.text,
            xs: 6,
            requireLabel: true,
            name: 'preferdLanguage',
            id: 'preferdLanguage',
            disabled: true,
          },
          {
            label: strings.selectedContactPreference,
            type: Constants.formInputType.dropdown,
            requireLabel: true,
            xs: 6,
            multiple: true,
            name: 'selectedContactPreferences',
            id: 'selectedContactPreferences',
            menuItems: dropdownMapper(
              contactPreferences,
              'contactPreferenceId',
              'name',
            ),
          },
          {
            name: 'image',
            uploadLabel: imageName,
            label: strings.uploadPicture,
            variant: outlined,
            type: uploadIconLabel,
            customClass: style.uploadIcon,
            startIcon: <UploadIcon />,
            xs: 6,
            onClick: showPictureUploadModal,
          },
          {
            type: uploadFile,
            fileUploadType: fileUploadTypes.signupResume,
            xs: 6,
          },
        ],
    },
    geographicAvailability: {
      title: strings.myGeographicAvailability.toUpperCase(),
      type: 'single',
      form: () =>
        [
          {
            label: strings.firstName,
            requireLabel: true,
            type: Constants.formInputType.accordion,
            xs: 12,
            items: regions,
          },
        ],
    },
    professionalInfo: [
      {
        title: strings.profissonalInfo.toUpperCase(),
        type: 'single',
        hideSave: true,
        form: () =>
          [
            isTechnician && {
              label: strings.diploma,
              infoText: strings.diplomaInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'diploma',
              id: 'diploma',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.educationalInstitute,
              infoText: strings.educationalInstituteInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'instituteName',
              id: 'instituteName',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.graduationYear,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'techGraduationYear',
              id: 'techGraduationYear',
              menuItems: years,
              requireLabel: true,
            },
            isPharmacist && {
              label: strings.licenseNo,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'licenseNumber',
              id: 'licenseNumber',
              requireLabel: true,
              maxLength: 8,
            },
            !isStudent && {
              label: isPharmacist
                ? strings.yearLicensed
                : strings.techExperienceSince,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'graduationYear',
              id: 'graduationYear',
              menuItems: years,
              requireLabel: true,
            },
            {
              label: strings.yearsOfExperience,
              xs: 6,
              key: 'yearsOfExperience',
              id: 'yearsOfExperience',
              type: Constants.formInputType.text,
              name: 'yearsOfExperience',
              disabled: true,
            },
            {
              label: strings.frenchLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'frenchLevel',
              id: 'frenchLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.englishLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'englishLevel',
              id: 'englishLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.otherLanguage,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'otherLanguages',
              id: 'otherLanguages',
            },
            {
              label: strings.professionalCapacity,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'pharmacistCapacityId',
              id: 'pharmacistCapacityId',
              menuItems: dropdownMapper(
                proUtils.pharmacistCapacities ?? [],
                'pharmacistCapacityId',
                'description',
              ),
              requireLabel: true,
            },
            isFullServiceTechnician && {
              label: strings.hospitalDischargeComfortLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'handlingHospitalDischargeRating',
              id: 'handlingHospitalDischargeRating',
              menuItems: hospitalDischargeComfortLevels,
            },
            isTechnician && {
              type: uploadFile,
              fileUploadType: fileUploadTypes.signupDiploma,
              xs: 6,
              customClass: style.diploma,
            },
            {
              label: strings.areYouIncorporated,
              type: Constants.formInputType.radio,
              xs: 6,
              horizontal: true,
              name: 'incorporated',
              id: 'incorporated',
              radioItems: [
                { label: strings.yes, value: true },
                { label: strings.no, value: false },
              ],
            },
            isFullServicePharmacistTechnician && {
              label: strings.familiarWithMethadonePreparation,
              type: Constants.formInputType.checkbox,
              xs: 6,
              customClass: style.checkbox,
              name: 'isMethadonePrepKnowledge',
              id: 'isMethadonePrepKnowledge',
            },
            isFullServicePharmacistTechnician && {
              label: isTechnician
                ? strings.dispillPreparation
                : strings.comfortableWithDispillVerification,
              type: Constants.formInputType.checkbox,
              customClass: style.checkbox,
              xs: 6,
              name: 'isComfortDispillVerification',
              id: 'isComfortDispillVerification',
            },
            isFullServicePharmacist && {
              label: strings.doYouHaveOwnDsqKey,
              type: Constants.formInputType.checkbox,
              customClass: style.checkbox,
              xs: 6,
              name: 'isDSQKeyAvailable',
              id: 'isDSQKeyAvailable',
            },
            // #technician-changes: technician-only answers.
            isFullServiceTechnician && {
              label: strings.nonSterileCompoundingKnowledge,
              type: Constants.formInputType.checkbox,
              customClass: style.checkbox,
              xs: 6,
              name: 'isNonSterilCompoundPrepKnowledge',
              id: 'isNonSterilCompoundPrepKnowledge',
            },
            isFullServiceTechnician && {
              label: strings.clicSanteKnowledge,
              type: Constants.formInputType.checkbox,
              customClass: style.checkbox,
              xs: 6,
              name: 'isClickSanteKnowledge',
              id: 'isClickSanteKnowledge',
            },
            isFullServiceTechnician && {
              label: strings.empegoKnowledge,
              type: Constants.formInputType.checkbox,
              xs: 6,
              customClass: style.checkbox,
              name: 'isEmpegoKnowledge',
              id: 'isEmpegoKnowledge',
            },
          ],
      },
    ],
    selectedSoftwares: {
      title: strings.softwareFamiliarity.toUpperCase(),
      type: 'table',
      form: () =>
        ({
          options: [
            { key: '0', label: '0%' },
            { key: '1', label: '25%' },
            { key: '2', label: '50%' },
            { key: '3', label: '75%' },
            { key: '4', label: '100%' },
          ],
          rows: proUtils?.softwares,
        }),
    },
    contacts: [
      {
        title: strings.addressPhoneNo,
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              label: strings.address,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 50,
              name: 'address',
              id: 'address',
              requireLabel: true,
            },
            {
              label: strings.city,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'city',
              id: 'city',
              requireLabel: true,
            },
            {
              label: strings.province,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'province',
              id: 'province',
              requireLabel: true,
              menuItems: dropdownMapper(proUtils.provinces ?? [], 'key', 'name'),
            },
            {
              label: `${strings.postalCode} (${strings.postalCodeHint})`,
              type: Constants.formInputType.text,
              xs: 6,
              customType: InputTypes.postalCode,
              name: 'postalCode',
              capitalize: true,
              id: 'postalCode',
              requireLabel: true,
              placeholder: ' ',
            },
            {
              label: strings.homePhone,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'phone',
              id: 'phone',
              requireLabel: true,
            },
            {
              label: strings.mobile,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'mobile',
              id: 'mobile',
              requireLabel: true,
            },
            {
              label: strings.agreeOn,
              linkLabel: strings.termsAndConditions,
              handleChange,
              onLinkClick: showTermsAndConditions,
              type: Constants.formInputType.checkbox,
              xs: 12,
              name: 'termsAndConditions',
              id: 'termsAndConditions',
              requireLabel: true,
              customClass: {
                color: '#4A5056',
              },
            },
            ...crossPlatformSignupFields(),
          ],
      },
    ],
  });
};
export const professionalSignupFields = ({
  proUtils,
  regions,
  showTermsAndConditions,
  handleChange,
  contactPreferences,
  showPictureUploadModal,
  imageName,
  isIncorporated,
  userType,
}: {
  proUtils: IUtils;
  regions: IAllRegions;
  showTermsAndConditions: () => void;
  handleChange: (e: boolean) => void;
  contactPreferences: IContactPreferences[];
  showPictureUploadModal: () => void;
  imageName: string;
  isIncorporated: boolean;
  userType: TypeOfUser;
}): IPharmacistSignupForm => {
  const isPharmacist = userType === TypeOfUser.pharmacist;
  const isTechnician = userType === TypeOfUser.technician;
  const isStudent = userType === TypeOfUser.student;

  const incorporatedInfo = [
    {
      label: strings.companyName,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateCompanyName',
      id: 'corporateCompanyName',
      requireLabel: true,
    },
    {
      label: strings.streetAddress,
      type: Constants.formInputType.text,
      xs: 6,
      maxLength: 50,
      name: 'corporateStreetAddress',
      id: 'corporateStreetAddress',
    },
    {
      label: strings.city,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporateCity',
      id: 'corporateCity',
    },
    {
      label: strings.province,
      xs: 6,
      name: 'corporateProvince',
      id: 'corporateProvince',
      type: Constants.formInputType.dropdown,
      menuItems: dropdownMapper(proUtils.provinces ?? [], 'key', 'name'),
    },
    {
      type: Constants.formInputType.text,
      xs: 6,
      name: 'corporatePostalCode',
      id: 'corporatePostalCode',
      capitalize: true,
      customType: InputTypes.postalCode,
      label: `${strings.postalCode} (${strings.postalCodeHint})`,
      maxLength: 10,
      placeholder: ' ',
    },
    {
      label: strings.telephoneNumber,
      xs: 6,
      name: 'corporatePhone',
      id: 'corporatePhone',
      type: Constants.formInputType.masked,
      mask: telephoneInputMask,
    },
    {
      label: strings.tpsNumber,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tps',
      id: 'tps',
      requireLabel: true,
    },
    {
      label: strings.tvqNumber,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tvq',
      id: 'tvq',
      requireLabel: true,
    },
  ];

  const nonIncorporatedInfo = [
    {
      label: strings.formatString(
        strings.ifApplicableFormat,
        strings.tpsNumber,
      ) as string,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tps',
      id: 'tps',
    },
    {
      label: strings.formatString(
        strings.ifApplicableFormat,
        strings.tvqNumber,
      ) as string,
      type: Constants.formInputType.text,
      xs: 6,
      name: 'tvq',
      id: 'tvq',
    },
  ];

  let info: any[] = [];
  if (isIncorporated) {
    info = incorporatedInfo;
  } else {
    info = nonIncorporatedInfo;
  }

  return {
    general: {
      title: strings.general.toUpperCase(),
      type: 'single',
      form: () =>
        [
          {
            name: 'firstName',
            label: strings.firstName,
            requireLabel: true,
            type: Constants.formInputType.text,
            xs: 6,
          },
          {
            name: 'lastName',
            label: strings.lastName,
            requireLabel: true,
            type: Constants.formInputType.text,
            xs: 6,
          },
          {
            label: strings.preferredLanguage,
            type: Constants.formInputType.text,
            xs: 6,
            requireLabel: true,
            name: 'preferdLanguage',
            id: 'preferdLanguage',
            disabled: true,
          },
          {
            label: strings.selectedContactPreference,
            type: Constants.formInputType.dropdown,
            requireLabel: true,
            xs: 6,
            multiple: true,
            name: 'selectedContactPreferences',
            id: 'selectedContactPreferences',
            menuItems: dropdownMapper(
              contactPreferences,
              'contactPreferenceId',
              'name',
            ),
          },
          {
            name: 'image',
            uploadLabel: imageName,
            label: strings.uploadPicture,
            variant: outlined,
            type: uploadIconLabel,
            customClass: style.uploadIcon,
            startIcon: <UploadIcon />,
            xs: 6,
            onClick: showPictureUploadModal,
          },
          {
            type: uploadFile,
            fileUploadType: fileUploadTypes.signupResume,
            xs: 6,
          },
        ],
    },
    geographicAvailability: {
      title: strings.myGeographicAvailability.toUpperCase(),
      type: 'single',
      form: () =>
        [
          {
            label: strings.firstName,
            requireLabel: true,
            type: Constants.formInputType.accordion,
            xs: 12,
            items: regions,
          },
        ],
    },
    professionalInfo: [
      {
        title: strings.profissonalInfo.toUpperCase(),
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            isTechnician && {
              label: strings.diploma,
              infoText: strings.diplomaInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'diploma',
              id: 'diploma',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.educationalInstitute,
              infoText: strings.educationalInstituteInfo,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 300,
              name: 'instituteName',
              id: 'instituteName',
              requireLabel: true,
            },
            isTechnician && {
              label: strings.graduationYear,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'techGraduationYear',
              id: 'techGraduationYear',
              menuItems: years,
              requireLabel: true,
            },
            isPharmacist && {
              label: strings.licenseNo,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'licenseNumber',
              id: 'licenseNumber',
              requireLabel: true,
              maxLength: 8,
            },
            !isStudent && {
              label: isPharmacist
                ? strings.yearLicensed
                : strings.techExperienceSince,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'graduationYear',
              id: 'graduationYear',
              menuItems: years,
              requireLabel: true,
            },
            {
              label: strings.frenchLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'frenchLevel',
              id: 'frenchLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.englishLevel,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'englishLevel',
              id: 'englishLevel',
              menuItems: LanguageLevel(),
            },
            {
              label: strings.otherLanguage,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'otherLanguages',
              id: 'otherLanguages',
            },
            {
              label: strings.rxVolumeComfort,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'pharmacistCapacityId',
              id: 'pharmacistCapacityId',
              menuItems: dropdownMapper(
                proUtils.pharmacistCapacities ?? [],
                'pharmacistCapacityId',
                'description',
              ),
              requireLabel: true,
            },
            isTechnician && {
              type: uploadFile,
              fileUploadType: fileUploadTypes.signupDiploma,
              xs: 6,
              customClass: style.diploma,
            },
          ],
      },
      {
        title: strings.incorporated.toUpperCase(),
        hideSave: true,
        form: () =>
          [
            {
              label: strings.areYouIncorporated,
              type: Constants.formInputType.radio,
              xs: 12,
              style: style.radio,
              name: 'incorporated',
              id: 'incorporated',
              horizontal: true,
              radioItems: [
                { label: strings.yes, value: true },
                { label: strings.no, value: false },
              ],
            },
            ...info,
          ],
      },
    ],
    selectedSoftwares: {
      title: strings.softwareFamiliarity.toUpperCase(),
      type: 'table',
      form: () =>
        ({
          options: [
            { key: '0', label: '0%' },
            { key: '1', label: '25%' },
            { key: '2', label: '50%' },
            { key: '3', label: '75%' },
            { key: '4', label: '100%' },
          ],
          rows: proUtils?.softwares,
        }),
    },
    contacts: [
      {
        title: strings.addressPhoneNo,
        type: 'multiple',
        hideSave: true,
        form: () =>
          [
            {
              label: strings.address,
              type: Constants.formInputType.text,
              xs: 6,
              maxLength: 50,
              name: 'address',
              id: 'address',
              requireLabel: true,
            },
            {
              label: strings.city,
              type: Constants.formInputType.text,
              xs: 6,
              name: 'city',
              id: 'city',
              requireLabel: true,
            },
            {
              label: strings.province,
              type: Constants.formInputType.dropdown,
              xs: 6,
              name: 'province',
              id: 'province',
              requireLabel: true,
              menuItems: dropdownMapper(proUtils.provinces ?? [], 'key', 'name'),
            },
            {
              label: `${strings.postalCode} (${strings.postalCodeHint})`,
              type: Constants.formInputType.text,
              xs: 6,
              customType: InputTypes.postalCode,
              name: 'postalCode',
              capitalize: true,
              id: 'postalCode',
              requireLabel: true,
              placeholder: ' ',
            },
            {
              label: strings.homePhone,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'phone',
              id: 'phone',
              requireLabel: true,
            },
            {
              label: strings.mobile,
              type: Constants.formInputType.masked,
              mask: telephoneInputMask,
              xs: 6,
              name: 'mobile',
              id: 'mobile',
              requireLabel: true,
            },
            {
              label: strings.agreeOn,
              linkLabel: strings.termsAndConditions,
              handleChange,
              onLinkClick: showTermsAndConditions,
              type: Constants.formInputType.checkbox,
              xs: 12,
              name: 'termsAndConditions',
              id: 'termsAndConditions',
              requireLabel: true,
              customClass: {
                color: '#4A5056',
              },
            },
            ...crossPlatformSignupFields(),
          ],
      },
    ],
  };
};
